import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { useRouteError, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';

const CHUNK_RELOAD_KEY = 'chunk_error_reload_attempted';

const isChunkLoadError = (error: any): boolean => {
  if (!error) return false;
  const errorString = String(error?.message || error?.statusText || error);
  return (
    errorString.includes('ChunkLoadError') ||
    errorString.includes('Loading chunk') ||
    errorString.includes('Loading CSS chunk') ||
    errorString.includes('Failed to fetch dynamically imported module')
  );
};

const ErrorBoundary = () => {
  const error = useRouteError() as any;
  const navigate = useNavigate();
  const theme = useTheme();
  const [isReloading, setIsReloading] = useState(false);

  const chunkError = isChunkLoadError(error);

  useEffect(() => {
    if (chunkError) {
      const hasReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY);
      
      if (!hasReloaded) {
        // Mark that we're attempting a reload
        sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true');
        setIsReloading(true);
        
        // Small delay to show the loading state
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    }
  }, [chunkError]);

  console.error('Application Error:', error);

  // Show loading spinner while auto-reloading for chunk errors
  if (isReloading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: 4,
          backgroundColor: theme.palette.background.default
        }}
      >
        <CircularProgress size={48} sx={{ mb: 3 }} />
        <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
          Updating application...
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
          Please wait while we load the latest version.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 4,
        backgroundColor: theme.palette.background.default
      }}
    >
      <Box
        sx={{
          maxWidth: 600,
          textAlign: 'center',
          padding: 4,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[3]
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            margin: '0 auto 24px',
            borderRadius: '50%',
            backgroundColor: theme.palette.error.lighter,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              color: theme.palette.error.main
            }}
          >
            ⚠️
          </Box>
        </Box>

        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          {chunkError ? 'Update Required' : 'Oops! Something went wrong'}
        </Typography>

        <Typography variant="body1" sx={{ mb: 3, color: theme.palette.text.secondary }}>
          {chunkError
            ? 'A new version of the application is available. Please reload the page to get the latest updates.'
            : "We're sorry for the inconvenience. An unexpected error has occurred."}
        </Typography>

        {process.env.NODE_ENV === 'development' && error && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: theme.palette.grey[100],
              borderRadius: 1,
              textAlign: 'left',
              overflow: 'auto',
              maxHeight: 200
            }}
          >
            <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {error.statusText || error.message || 'Unknown error'}
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => {
              sessionStorage.removeItem(CHUNK_RELOAD_KEY);
              window.location.reload();
            }}
            sx={{
              borderRadius: '8px',
              fontSize: 14,
              fontWeight: 600,
              height: 40,
              px: 3
            }}
          >
            Reload Page
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              sessionStorage.removeItem(CHUNK_RELOAD_KEY);
              navigate('/dashboard');
            }}
            sx={{
              borderRadius: '8px',
              fontSize: 14,
              fontWeight: 600,
              height: 40,
              px: 3
            }}
          >
            Go to Dashboard
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ErrorBoundary;
